import React, { useState } from 'react';
import { PhoneForwarded, Check, ChevronDown, ChevronUp, Smartphone, HelpCircle, Search, Monitor, Phone } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';

const CARRIERS = [
  {
    name: 'AT&T',
    logo: 'AT&T',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
      { type: 'Unreachable', activate: '**62*{number}#', deactivate: '##62#' },
    ],
    steps: [
      'Open your Phone app and dial the activation code below',
      'Replace the number with your Miadesk number',
      'Press the Call button and wait for the confirmation tone',
      'You will hear a confirmation tone or see a success message',
    ],
    notes: 'Codes work on both postpaid and prepaid AT&T plans. For AT&T Business, you may need to enable via the myAT&T portal.',
  },
  {
    name: 'Verizon',
    logo: 'VZ',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
      { type: 'When Busy', activate: '*90{number}', deactivate: '*91' },
      { type: 'No Answer', activate: '*71{number}', deactivate: '*73' },
    ],
    steps: [
      'Dial *72 followed by your Miadesk number',
      'Press the Call button',
      'Wait for a confirmation tone or two short beeps',
      'Forwarding is now active — all calls will route to Miadesk',
    ],
    notes: 'On Verizon Wireless, wait for the call to connect and hear a confirmation beep before hanging up. For My Verizon app: Account > Manage Device > Call Forwarding.',
  },
  {
    name: 'T-Mobile',
    logo: 'T-Mo',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
      { type: 'Unreachable', activate: '**62*{number}#', deactivate: '##62#' },
      { type: 'Cancel All', activate: '', deactivate: '##002#' },
    ],
    steps: [
      'Open your Phone app and dial the activation code',
      'Add your Miadesk number after the *',
      'Press Call and wait for the confirmation message',
      'To cancel ALL forwarding at once, dial ##002#',
    ],
    notes: 'T-Mobile uses GSM standard codes. The ##002# code cancels all conditional forwarding types at once.',
  },
  {
    name: 'Sprint (now T-Mobile)',
    logo: 'SP',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*720' },
      { type: 'When Busy', activate: '*74{number}', deactivate: '*740' },
      { type: 'No Answer', activate: '*73{number}', deactivate: '*730' },
    ],
    steps: [
      'Dial *72 from your Sprint phone',
      'Enter your Miadesk phone number',
      'Press Call and wait for two beeps',
      'Forwarding is now active',
    ],
    notes: 'Sprint has merged with T-Mobile. If these codes don\'t work, try the T-Mobile codes above.',
  },
  {
    name: 'US Cellular',
    logo: 'USC',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*720' },
      { type: 'When Busy', activate: '*90{number}', deactivate: '*91' },
      { type: 'No Answer', activate: '*92{number}', deactivate: '*93' },
    ],
    steps: [
      'Dial *72 followed by your Miadesk number',
      'Press the Call button',
      'Wait for a confirmation tone',
      'Calls will now forward to your Miadesk AI receptionist',
    ],
    notes: 'Call forwarding can also be managed via the My UScellular app or online account portal.',
  },
  {
    name: 'Cricket Wireless',
    logo: 'CW',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
      { type: 'Unreachable', activate: '**62*{number}#', deactivate: '##62#' },
    ],
    steps: [
      'Open the Phone dialer and enter the activation code',
      'Replace {number} with your full Miadesk number',
      'Press Call and listen for the confirmation tone',
      'Use the deactivation code when you want to turn off forwarding',
    ],
    notes: 'Cricket uses AT&T network, so AT&T forwarding codes work. You can also manage via myCricket app > Account Settings.',
  },
  {
    name: 'Boost Mobile',
    logo: 'BST',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
    ],
    steps: [
      'Dial *72 plus your Miadesk number',
      'Press Call and wait for a confirmation tone or beep',
      'Forwarding is now active',
      'To deactivate, dial *73',
    ],
    notes: 'Boost now runs on the T-Mobile network. If *72 doesn\'t work, try the T-Mobile GSM codes (**21*number#).',
  },
  {
    name: 'Metro by T-Mobile',
    logo: 'MTR',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
      { type: 'Unreachable', activate: '**62*{number}#', deactivate: '##62#' },
      { type: 'Cancel All', activate: '', deactivate: '##002#' },
    ],
    steps: [
      'Open the Phone dialer',
      'Enter the activation code with your Miadesk number',
      'Press Call',
      'Wait for a success notification or confirmation tone',
    ],
    notes: 'Metro uses the T-Mobile network and supports all GSM forwarding codes. ##002# cancels all forwarding at once.',
  },
  {
    name: 'Google Fi',
    logo: 'GFi',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
    ],
    steps: [
      'Open the Phone app on your Google Fi device',
      'Dial the activation code with your Miadesk number',
      'Press Call and wait for the confirmation',
      'Alternatively, go to fi.google.com > Phone Settings > Call Forwarding',
    ],
    notes: 'Google Fi also supports setting up forwarding via the Fi app: Settings > Phone Settings > Call Forwarding.',
  },
  {
    name: 'Mint Mobile',
    logo: 'MNT',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
      { type: 'Unreachable', activate: '**62*{number}#', deactivate: '##62#' },
    ],
    steps: [
      'Dial the activation code from your Mint Mobile phone',
      'Include your full Miadesk number',
      'Press Call and wait for confirmation',
      'Use ##002# to cancel all forwarding at once',
    ],
    notes: 'Mint Mobile uses the T-Mobile network, so all GSM forwarding codes apply.',
  },
  {
    name: 'Visible',
    logo: 'VIS',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
    ],
    steps: [
      'Dial *72 followed by your Miadesk number',
      'Press the Call button',
      'Wait for a confirmation tone',
      'To deactivate, dial *73 and press Call',
    ],
    notes: 'Visible runs on the Verizon network. Use Verizon-style forwarding codes.',
  },
  {
    name: 'Tracfone',
    logo: 'TF',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
      { type: 'All Calls (GSM)', activate: '**21*{number}#', deactivate: '##21#' },
    ],
    steps: [
      'Try *72 + your Miadesk number first (CDMA phones)',
      'If that doesn\'t work, try **21*number# (GSM/LTE phones)',
      'Press Call and wait for confirmation',
      'Deactivate with *73 or ##21# respectively',
    ],
    notes: 'Tracfone uses multiple networks. Try the CDMA code (*72) first, then the GSM code (**21*) if it fails.',
  },
  {
    name: 'Straight Talk',
    logo: 'ST',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
      { type: 'All Calls (GSM)', activate: '**21*{number}#', deactivate: '##21#' },
    ],
    steps: [
      'Dial *72 + your Miadesk number (Verizon/CDMA phones)',
      'Or dial **21*number# (T-Mobile/AT&T/GSM phones)',
      'Press Call and wait for confirmation',
      'Deactivate with *73 or ##21# respectively',
    ],
    notes: 'Straight Talk operates on multiple networks. The code depends on which network your phone uses.',
  },
  {
    name: 'Consumer Cellular',
    logo: 'CC',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
    ],
    steps: [
      'Dial the activation code from your phone',
      'Add your Miadesk number in place of {number}',
      'Press Call and wait for the confirmation',
      'Contact Consumer Cellular support if codes don\'t work on your device',
    ],
    notes: 'Consumer Cellular uses both AT&T and T-Mobile towers. GSM codes should work for most devices.',
  },
  {
    name: 'Spectrum Mobile',
    logo: 'SPM',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
    ],
    steps: [
      'Dial *72 followed by your Miadesk number',
      'Press Call and wait for confirmation',
      'To deactivate: Dial *73',
      'You can also manage forwarding via the My Spectrum app',
    ],
    notes: 'Spectrum Mobile uses the Verizon network. You can also set up forwarding through the My Spectrum app or SpectrumMobile.com.',
  },
  {
    name: 'Xfinity Mobile',
    logo: 'XF',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
    ],
    steps: [
      'Open the Xfinity Mobile app or go to xfinity.com/mobile',
      'Navigate to Account > Phone Settings > Call Forwarding',
      'Enable "Forward All Calls" and enter your Miadesk number',
      'Alternatively, dial *72 + your Miadesk number from the phone dialer',
    ],
    notes: 'Xfinity Mobile uses the Verizon network. App-based setup is recommended for the most reliable experience.',
  },
  {
    name: 'H2O Wireless',
    logo: 'H2O',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'When Busy', activate: '**67*{number}#', deactivate: '##67#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
    ],
    steps: [
      'Open the Phone dialer and enter the activation code',
      'Include your Miadesk number',
      'Press Call and wait for confirmation',
      'Uses AT&T network — AT&T GSM codes apply',
    ],
    notes: 'H2O Wireless runs on the AT&T network. All AT&T GSM forwarding codes work.',
  },
  {
    name: 'US Mobile',
    logo: 'USM',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls (GSM)', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'All Calls (Warp)', activate: '*72{number}', deactivate: '*73' },
    ],
    steps: [
      'For GSM (T-Mobile) SIM: Dial **21*number#',
      'For Warp 5G (Verizon) SIM: Dial *72 + number',
      'Press Call and wait for confirmation',
      'Deactivate using the corresponding code for your SIM type',
    ],
    notes: 'US Mobile offers both T-Mobile (GSM) and Verizon (Warp 5G) SIMs. Use the correct code for your SIM type.',
  },
  {
    name: 'Tello',
    logo: 'TEL',
    category: 'mobile',
    forwarding: [
      { type: 'All Calls', activate: '**21*{number}#', deactivate: '##21#' },
      { type: 'No Answer', activate: '**61*{number}#', deactivate: '##61#' },
    ],
    steps: [
      'Dial the forwarding code from your Tello phone',
      'Include your full Miadesk number',
      'Press Call and listen for a confirmation',
      'Manage forwarding from your Tello dashboard at tello.com',
    ],
    notes: 'Tello uses the T-Mobile network. Standard GSM forwarding codes apply.',
  },
  {
    name: 'Landline (Traditional)',
    logo: 'LL',
    category: 'landline',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
      { type: 'When Busy', activate: '*90{number}', deactivate: '*91' },
      { type: 'No Answer', activate: '*92{number}', deactivate: '*93' },
    ],
    steps: [
      'Pick up the handset and listen for a dial tone',
      'Dial *72 followed by your full Miadesk number',
      'Wait for a stutter dial tone or confirmation beeps (usually 2 short beeps)',
      'Hang up — forwarding is now active',
    ],
    notes: 'Works with most US landline providers (AT&T, CenturyLink, Frontier, Windstream). If *72 doesn\'t work, contact your provider.',
  },
  {
    name: 'Vonage',
    logo: 'VNG',
    category: 'voip',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
    ],
    steps: [
      'Log in to your Vonage dashboard at my.vonage.com',
      'Go to Phone Settings > Call Forwarding',
      'Enter your Miadesk number and enable forwarding',
      'Alternatively, dial *72 + your Miadesk number from a Vonage handset',
    ],
    notes: 'Vonage supports both dialer codes and web portal configuration. Web portal changes take effect immediately.',
  },
  {
    name: 'Ooma',
    logo: 'OMA',
    category: 'voip',
    forwarding: [
      { type: 'All Calls', activate: '*72{number}', deactivate: '*73' },
    ],
    steps: [
      'Log in to my.ooma.com',
      'Navigate to Preferences > Call Forwarding',
      'Enter your Miadesk number and toggle forwarding on',
      'You can also dial *72 + number from an Ooma handset',
    ],
    notes: 'Ooma Office users can set up forwarding for individual extensions or the main number via the admin portal.',
  },
  {
    name: 'RingCentral',
    logo: 'RC',
    category: 'voip',
    forwarding: [
      { type: 'All Calls', activate: 'Portal', deactivate: 'Portal' },
    ],
    steps: [
      'Log in to your RingCentral admin portal',
      'Go to Phone System > Company Numbers or your Extension',
      'Click Call Forwarding & Screening',
      'Add your Miadesk number as an external forwarding number',
      'Set the number of rings before forwarding and save',
    ],
    notes: 'RingCentral does not support star codes for forwarding. All configuration must be done through the web portal or mobile app.',
  },
  {
    name: 'Google Voice',
    logo: 'GV',
    category: 'voip',
    forwarding: [
      { type: 'All Calls', activate: 'Portal', deactivate: 'Portal' },
    ],
    steps: [
      'Open Google Voice at voice.google.com',
      'Click the Settings gear icon',
      'Under "Calls", find "Forward calls to" section',
      'Add your Miadesk number and verify it via the code Google sends',
      'Toggle the forwarding number on',
    ],
    notes: 'Google Voice requires number verification before forwarding. You\'ll receive a verification call or text to your Miadesk number.',
  },
  {
    name: 'iPhone (iOS Settings)',
    logo: 'iOS',
    category: 'device',
    forwarding: [
      { type: 'All Calls', activate: 'Settings', deactivate: 'Settings' },
    ],
    steps: [
      'Open Settings on your iPhone',
      'Tap Phone > Call Forwarding',
      'Toggle Call Forwarding on',
      'Enter your Miadesk number in the "Forward To" field',
      'Tap Back — forwarding is now active (a phone icon appears in the status bar)',
    ],
    notes: 'This method works on all carriers. When call forwarding is active, you\'ll see a phone icon in the status bar. To disable, toggle Call Forwarding off in the same menu.',
  },
  {
    name: 'Android (Phone Settings)',
    logo: 'AND',
    category: 'device',
    forwarding: [
      { type: 'All Calls', activate: 'Settings', deactivate: 'Settings' },
    ],
    steps: [
      'Open the Phone app',
      'Tap the three-dot menu (or More) > Settings',
      'Tap Calls > Call Forwarding (or Supplementary Services)',
      'Select "Always Forward" and enter your Miadesk number',
      'Tap Enable or Turn On',
    ],
    notes: 'Android menus vary by manufacturer (Samsung, Pixel, OnePlus, etc.). Look for Call Forwarding under Phone app settings or device Settings > Connections > Call Settings.',
  },
];

const CATEGORIES = [
  { key: 'all', label: 'All Carriers' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'landline', label: 'Landline' },
  { key: 'voip', label: 'VoIP' },
  { key: 'device', label: 'Device Settings' },
];

const CallForwardingSetup = ({ miadeskNumber }) => {
  const [expandedCarrier, setExpandedCarrier] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const displayNumber = miadeskNumber || '+1 (555) XXX-XXXX';
  const rawNumber = displayNumber.replace(/\D/g, '');

  const filteredCarriers = CARRIERS.filter(c => {
    const matchCategory = categoryFilter === 'all' || c.category === categoryFilter;
    const matchSearch = !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  const formatCode = (code) => code.replace('{number}', rawNumber);

  return (
    <div className="space-y-6" data-testid="call-forwarding-setup">
      {/* Header */}
      <div className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/20">
          <PhoneForwarded className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Set Up Call Forwarding</h2>
        <p className="text-gray-400 max-w-lg mx-auto">
          Forward your existing business number to Miadesk so your AI receptionist answers every call.
        </p>
      </div>

      {/* Your Miadesk Number */}
      <Card className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-500/20">
        <CardContent className="py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-purple-400 font-medium uppercase tracking-wider">Your Miadesk Number</p>
            <p className="text-2xl font-bold text-white font-mono mt-1" data-testid="miadesk-number">{displayNumber}</p>
          </div>
          <Smartphone className="w-8 h-8 text-purple-400/50" />
        </CardContent>
      </Card>

      {/* How it works */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { step: '1', title: 'Get Your Number', desc: 'Purchase a Miadesk number or use the one assigned to your account' },
          { step: '2', title: 'Forward Your Line', desc: 'Set up call forwarding from your business number using the instructions below' },
          { step: '3', title: 'Calls Go to Mia', desc: 'Your AI receptionist answers every call 24/7 — never miss a lead again' },
        ].map(s => (
          <div key={s.step} className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
            <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center mx-auto mb-3 text-sm font-bold">{s.step}</div>
            <p className="text-white font-medium text-sm mb-1">{s.title}</p>
            <p className="text-gray-500 text-xs">{s.desc}</p>
          </div>
        ))}
      </div>

      {/* Search and Category Filter */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search carrier..."
            className="bg-gray-900/50 border-white/10 text-white pl-10 h-10"
            data-testid="carrier-search"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setCategoryFilter(cat.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                categoryFilter === cat.key
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                  : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/20'
              }`}
              data-testid={`category-${cat.key}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Carrier Instructions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white">Select Your Carrier</h3>
          <span className="text-xs text-gray-500">{filteredCarriers.length} carrier{filteredCarriers.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="space-y-2">
          {filteredCarriers.map((carrier, i) => {
            const globalIdx = CARRIERS.indexOf(carrier);
            const expanded = expandedCarrier === globalIdx;
            return (
              <div key={globalIdx} className="bg-gray-900/50 border border-white/10 rounded-lg overflow-hidden" data-testid={`carrier-${carrier.name.replace(/[\s\/()]+/g, '-').toLowerCase()}`}>
                <button
                  onClick={() => setExpandedCarrier(expanded ? null : globalIdx)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  data-testid={`carrier-btn-${carrier.name.replace(/[\s\/()]+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-[10px] ${
                      carrier.category === 'voip' ? 'bg-blue-500/20' :
                      carrier.category === 'device' ? 'bg-emerald-500/20' :
                      carrier.category === 'landline' ? 'bg-amber-500/20' :
                      'bg-white/10'
                    }`}>
                      {carrier.logo}
                    </div>
                    <div className="text-left">
                      <span className="text-white font-medium text-sm">{carrier.name}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-gray-500 capitalize bg-white/5 px-1.5 py-0.5 rounded">{carrier.category}</span>
                        {carrier.forwarding[0] && carrier.forwarding[0].activate !== 'Portal' && carrier.forwarding[0].activate !== 'Settings' && (
                          <span className="text-gray-500 text-[10px] font-mono">{carrier.forwarding[0].activate.replace('{number}', '')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </button>

                {expanded && (
                  <div className="px-4 pb-4 border-t border-white/5">
                    {/* Steps */}
                    <ol className="space-y-3 mt-4">
                      {carrier.steps.map((step, si) => (
                        <li key={si} className="flex gap-3">
                          <div className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                            {si + 1}
                          </div>
                          <p className="text-gray-300 text-sm">{step}</p>
                        </li>
                      ))}
                    </ol>

                    {/* Forwarding Codes Table */}
                    <div className="mt-4 rounded-lg overflow-hidden border border-white/10">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-white/5">
                            <th className="text-left px-3 py-2 text-gray-400 text-xs font-medium">Type</th>
                            <th className="text-left px-3 py-2 text-gray-400 text-xs font-medium">Activate</th>
                            <th className="text-left px-3 py-2 text-gray-400 text-xs font-medium">Deactivate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {carrier.forwarding.map((fwd, fi) => (
                            <tr key={fi} className="border-t border-white/5">
                              <td className="px-3 py-2 text-gray-300 text-xs">{fwd.type}</td>
                              <td className="px-3 py-2">
                                {fwd.activate ? (
                                  <code className="text-teal-400 text-xs font-mono bg-teal-500/10 px-1.5 py-0.5 rounded">{formatCode(fwd.activate)}</code>
                                ) : (
                                  <span className="text-gray-500 text-xs">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <code className="text-red-400 text-xs font-mono bg-red-500/10 px-1.5 py-0.5 rounded">{fwd.deactivate}</code>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Carrier Notes */}
                    {carrier.notes && (
                      <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg">
                        <p className="text-xs text-blue-300">{carrier.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Help */}
      <div className="p-4 bg-amber-500/5 border border-amber-500/10 rounded-lg flex gap-3">
        <HelpCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-amber-400 font-medium">Need help?</p>
          <p className="text-gray-400 mt-1">
            If call forwarding doesn't work with the codes above, contact your carrier's support and ask them to set up
            "Unconditional Call Forwarding" to <span className="text-white font-mono">{displayNumber}</span>.
            Most carriers can enable this on their end within minutes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CallForwardingSetup;
